/**
 * Arch-order pairing of segmented teeth for the geodesic gum trim.
 *
 * Teeth are sorted by angle around the arch centroid (XZ plane); consecutive
 * pairs follow the dental arch, and the widest angular gap — the open back of
 * the horseshoe between the two last molars — is never paired. The trim seeds
 * bridge lines between paired seam centroids so a missing-tooth gap doesn't
 * split the gum collar.
 */

export type XZ = { x: number; z: number };

/** Index pairs of arch-consecutive teeth (input order preserved in indices). */
export function consecutiveArchPairs(centroids: XZ[]): Array<[number, number]> {
  if (centroids.length < 2) return [];
  if (centroids.length === 2) return [[0, 1]];

  const mean = { x: 0, z: 0 };
  for (const c of centroids) {
    mean.x += c.x;
    mean.z += c.z;
  }
  mean.x /= centroids.length;
  mean.z /= centroids.length;

  const sorted = centroids
    .map((c, index) => ({ index, angle: Math.atan2(c.z - mean.z, c.x - mean.x) }))
    .sort((a, b) => a.angle - b.angle);

  let widestGap = -1;
  let widestIdx = 0;
  for (let i = 0; i < sorted.length; i++) {
    const next = sorted[(i + 1) % sorted.length];
    let gap = next.angle - sorted[i].angle;
    if (gap < 0) gap += Math.PI * 2;
    if (gap > widestGap) {
      widestGap = gap;
      widestIdx = i;
    }
  }

  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i === widestIdx) continue;
    pairs.push([sorted[i].index, sorted[(i + 1) % sorted.length].index]);
  }
  return pairs;
}
